// RecentEntries — the last ten entries of the SAME KIND, under the form that
// books the next one.
//
// Operator, 17 Sept 2026: "also need latest 10 entries for that particular
// trade type at the trade booking screens/move/record — 10 last tx of that
// same tx type happened in ascending order". Book trade shows the last ten
// trades of the direction being booked (buys or sells), of the pair once it
// is known, with the chosen party once one is picked; Record money the last
// ten receipts or the last ten payments, narrowed the same way; a cost, the
// owner's money, a balance moved between parties and Move money each their
// own last ten. NEWEST AT THE TOP (operator, 16 Oct 2026, on the drawings
// list: "why it's showing in reverse order" — then "Newest first", every Last
// 10, as every other list in the app reads). Until then the newest came last,
// by his 17 Sept words ("in ascending order"), so the entry about to be
// booked read as the next line of the list.
//
// One line per entry on the Ledger primitive's sizes and seats (LEDGER.*,
// DealRowLine, Delta, Amount), so the sizes the operator rejected on the
// statement cannot creep back here. No new route: with a party chosen the
// trades are that party's own — the party read Book trade already makes
// (api.partyDetail, the same query key, one cache entry) carries the party's
// last twenty deals, so the list is complete for the party and not cut from
// the log's one page; without a party they come from the same read the
// Trades log makes, which holds the newest 200 and says so when it runs dry.
// The money rows come from the movement ledger with its own kind and party
// filters; every posting's refreshMoneyViews() invalidates ['trades'],
// ['party'] and ['movements'], so the panel gains the entry just booked
// without a navigation. Pre-cutover mirror replays (`legacy2:` ids, the log's
// "Carried in") are not live trades and are left out.
//
// The panel never blocks the form: it renders beneath it, reads on its own,
// and a failed read is its own one red line.

import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type Direction, type MovementKind, type MovementRow } from '../lib/api';
import { fmtStamp } from '../lib/format';
import { partyName, railName, sameAsset, foldAsset } from '../lib/naming';
import { Amount, DealRowLine, Delta, LEDGER } from './Ledger';
import { Description, descriptionClamp, type StatementLine } from './MovementTable';
import { ReadFailed } from './kit';
import { Pending } from './Pending';
import { Section } from './Section';

const LIMIT = 10;
/** The page the Trades log reads — the same key, so the cache is shared. */
const TRADE_PAGE = 200;

/** A movement kind the panel lists with a sentence: a cost, the owner's
 *  money, a balance between parties. */
export type EntryKind = Extract<MovementKind, 'expense' | 'owner' | 'p2p'>;

type Props =
  | {
      kind: 'trades'; title: string; partySlug?: string; entity?: string;
      /** The direction being booked; the list holds only that direction. */
      direction?: Direction;
      /** The pair once both legs are known; the list holds only that pair. */
      pair?: { base: string; quote: string };
    }
  | { kind: 'money';  title: string; direction: 'in' | 'out'; partySlug?: string; entity?: string }
  | { kind: 'moves';  title: string; entity?: string }
  | {
      kind: 'entries'; title: string; movementKind: EntryKind; entity?: string;
      /** Narrow the owner's money to capital (in) or drawings (out). */
      direction?: 'in' | 'out';
    };

export function RecentEntries(props: Props) {
  const entity = props.entity ?? 'dubaiotc';
  const party = props.kind === 'trades' || props.kind === 'money' ? (props.partySlug || '') : '';
  const moneyKind: MovementKind = props.kind === 'money'
    ? (props.direction === 'in' ? 'money_in' : 'money_out')
    : props.kind === 'entries' ? props.movementKind : 'rail_move';
  const isTrades = props.kind === 'trades';
  // The owner's kind holds both capital and drawings; read a little more so
  // the ten of one side survive the split below.
  const side = props.kind === 'entries' ? props.direction : undefined;
  const readLimit = side ? LIMIT * 2 : LIMIT;

  // THE PARTY'S OWN DEALS when a party is chosen: complete for the party,
  // and the read Book trade already holds under this key.
  const partyDeals = useQuery({
    queryKey: ['party', party, 'balances'],
    queryFn:  () => api.partyDetail(party, entity),
    enabled:  isTrades && party.length > 0,
    staleTime: 30_000,
  });
  const trades = useQuery({
    queryKey: ['trades', 'log', entity, TRADE_PAGE],
    queryFn:  () => api.listTrades(entity, TRADE_PAGE),
    enabled:  isTrades && party.length === 0,
    staleTime: 60_000,
  });
  const moves = useQuery({
    queryKey: ['movements', 'recent', entity, moneyKind, party, readLimit],
    queryFn:  () => api.movements({ entity, kind: moneyKind, party: party || undefined, limit: readLimit }),
    enabled:  !isTrades,
    staleTime: 60_000,
  });
  const q = isTrades ? (party ? partyDeals : trades) : moves;

  let body: ReactNode;
  if (q.isError) {
    body = <ReadFailed what="this list" error={q.error} onRetry={() => void q.refetch()} tone="bad" className="px-2" />;
  } else if (!q.data) {
    body = <p className="px-2"><Pending inline /></p>;
  } else if (props.kind === 'trades') {
    const all = party ? (partyDeals.data?.deals ?? []) : (trades.data?.trades ?? []);
    const mine = all.filter((t) =>
      !t.deal_id.startsWith('legacy2:')
      && t.lifecycle !== 'voided'
      && (!props.direction || t.direction === props.direction)
      && (!props.pair || samePair(t, props.pair)));
    const rows = lastTen(mine, (t) => t.booked_at);
    body = rows.length === 0
      ? <Nothing words={party ? 'Nothing yet.' : 'None among the newest 200.'} />
      : (
        <div className="divide-y divide-tg-secondary">
          {rows.map((t) => <DealRowLine key={t.deal_id} deal={t} hideParty={!!party} />)}
        </div>
      );
  } else {
    // The route answers newest first, which is the order shown (16 Oct 2026).
    const rows = [...(moves.data?.rows ?? [])]
      .filter((m) => !side || m.direction === side)
      .slice(0, LIMIT);
    body = rows.length === 0 ? <Nothing /> : (
      <div className="divide-y divide-tg-secondary">
        {rows.map((m) => props.kind === 'money'
          ? <MoneyLine key={m.txn_id} row={m} />
          : props.kind === 'moves'
            ? <MoveLine key={m.txn_id} row={m} />
            : <EntryLine key={m.txn_id} row={m} entity={props.entity ?? 'dubaiotc'} sideNamed={!!side} />)}
      </div>
    );
  }

  return (
    <Section title={props.title}>
      {body}
    </Section>
  );
}

/** The deal's two assets are the pair's two, either way round. */
function samePair(t: { asset_take: string; asset_give: string }, pair: { base: string; quote: string }): boolean {
  return (sameAsset(t.asset_take, pair.base) && sameAsset(t.asset_give, pair.quote))
    || (sameAsset(t.asset_take, pair.quote) && sameAsset(t.asset_give, pair.base));
}

/** The last N by time, newest first (operator, 16 Oct 2026). */
function lastTen<T>(rows: T[], at: (r: T) => string): T[] {
  return [...rows]
    .sort((a, b) => Date.parse(at(b)) - Date.parse(at(a)))
    .slice(0, LIMIT);
}

function Nothing({ words = 'Nothing yet.' }: { words?: string }) {
  return <p className="px-2 py-2 text-body text-tg-hint">{words}</p>;
}

// The lines below are LEDGER's list line: two lines on the phone (the stamp
// and the name; then the figures), one grid row at lg+.
const LINE = `${LEDGER.line} ${LEDGER.lineGrid}`;
const LEAD = `${LEDGER.lineLead} ${LEDGER.lineSeat}`;
const TAIL = `${LEDGER.lineTail} ${LEDGER.lineSeat}`;
const STAMP = `${LEDGER.whenText} ${LEDGER.whenStampW} shrink-0`;

/** A receipt or a payment: stamp · party · signed amount · the account. */
function MoneyLine({ row }: { row: MovementRow }) {
  const signed = row.direction === 'out' ? -row.amount : row.amount;
  return (
    <div className={`${LINE} lg:grid-cols-[6.5rem_minmax(0,1fr)_auto_auto]`} title={row.words}>
      <div className={LEAD}>
        <span className={STAMP}>{fmtStamp(row.at)}</span>
        <span className="who min-w-0 line-clamp-2 break-words lg:line-clamp-1">{row.party ? partyName(row.party) : '—'}</span>
      </div>
      <div className={TAIL}>
        <Delta value={signed} asset={row.asset} code />
        <span className="text-tg-hint truncate max-w-[12rem]">{row.rail ? railName(row.rail) : '—'}</span>
      </div>
    </div>
  );
}

/** A move: stamp · from → to · amount. */
function MoveLine({ row }: { row: MovementRow }) {
  const from = row.rail ? railName(row.rail) : '—';
  const to   = row.counter_rail ? railName(row.counter_rail) : '—';
  return (
    <div className={`${LINE} lg:grid-cols-[6.5rem_minmax(0,1fr)_auto]`} title={row.words}>
      <div className={LEAD}>
        <span className={STAMP}>{fmtStamp(row.at)}</span>
        {/* Two lines below lg — the account that paid, then "→" the one that
            received — so neither name is cut; one line at lg+. */}
        <span className="text-tg-hint min-w-0 break-words lg:truncate">{from}<br className="lg:hidden" /> → {to}</span>
      </div>
      <div className={TAIL}>
        <Amount value={row.amount} asset={row.asset} />
      </div>
    </div>
  );
}

/** A cost, the owner's money, a balance between parties: stamp · the
 *  Money screen's own description (counterpart-led, no amount inside —
 *  "Network fee · Hot wallet · TRC", "Owner took out · Office cash",
 *  "Loki OTC → JP Office") · the signed amount once. The BFF's sentence
 *  carried the amount too, so every row said its figure twice (21 Sept
 *  2026); it rides on the row's title. */
function EntryLine({ row, entity, sideNamed = false }: { row: MovementRow; entity: string; sideNamed?: boolean }) {
  const signed = row.direction === 'out' ? -row.amount : row.direction === 'in' ? row.amount : 0;
  const line: StatementLine = { key: row.txn_id, m: row, asset: foldAsset(row.asset), net: signed || null, running: null };
  // The owner's money under "Last 10 drawings" / "Last 10 capital in": the title
  // says which side, so each row says WHERE — "Owner took out · Angadiya cash ·
  // India" was cut at the phone's edge before the account (21 Oct 2026 audit).
  const accountOnly = sideNamed && row.kind === 'owner' && !!row.rail;
  return (
    <div className={`${LINE} lg:grid-cols-[6.5rem_minmax(0,1fr)_auto]`} title={row.words}>
      <div className={LEAD}>
        <span className={STAMP}>{fmtStamp(row.at)}</span>
        <span className={`min-w-0 ${descriptionClamp(row)} break-words lg:line-clamp-1`}>
          {accountOnly
            ? <span className="who">{railName(row.rail!)}</span>
            : (
              <>
                <Description line={line} entity={entity} flow />
                {row.kind !== 'p2p' && row.rail && <span className="text-tg-hint"> · {railName(row.rail)}</span>}
              </>
            )}
        </span>
      </div>
      <div className={TAIL}>
        {signed === 0
          ? <Amount value={row.amount} asset={row.asset} />
          : <Delta value={signed} asset={row.asset} code />}
      </div>
    </div>
  );
}
